// Per-brand static-ad pipeline selector.
//
// 2026-07-31 — 'direct_overlay' RETIRED. It asked the image model for a
// deliberately text-free plate and composited every headline, rating, quote and
// CTA locally as SVG. Replaced by 'direct_image', where the model typesets the
// copy itself; only the logo is still composited, and it is never sent to the
// model.
//
// Legacy rows are NOT migrated (house rule: forward-only). The backend resolves
// anything that is not exactly 'html' to 'direct_image', so a brand still holding
// the old string lands on the new path by itself. That is why `normalise` below
// exists rather than a bare `|| 'direct_image'`: without it a brand row reading
// 'direct_overlay' would set `selected` to a value absent from LABELS, and the
// Select would render with nothing chosen while the badge claimed "Fallback".

import { useEffect, useState } from 'react';
import { Badge, Button, Card, CardBody, Heading, HStack, Select, Text, VStack, useToast } from '@chakra-ui/react';
import { apiJson } from '../../auth/apiFetch';
import type { Brand } from './types';

type Pipeline = 'direct_image' | 'html';
type Props = { brand: Brand; onChanged: () => void };

const LABELS: Record<Pipeline, string> = {
  direct_image: 'Direct image (AI renders the finished ad)',
  html: 'HTML renderer (legacy fallback)'
};

/** Mirror of the backend resolver: only 'html' is the fallback, everything else is direct. */
function normalise(value: string | null | undefined): Pipeline {
  return String(value ?? '').trim().toLowerCase() === 'html' ? 'html' : 'direct_image';
}

export function StaticImagePipelineCard({ brand, onChanged }: Props) {
  const persisted = normalise(brand.staticImagePipeline);
  const [selected, setSelected] = useState<Pipeline>(persisted);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setSelected(normalise(brand.staticImagePipeline));
  }, [brand._id, brand.staticImagePipeline]);

  const save = async () => {
    setBusy(true);
    try {
      await apiJson(`/api/brand/${brand._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staticImagePipeline: selected })
      });
      toast({ title: 'Static ad pipeline updated', status: 'success', duration: 2500 });
      onChanged();
    } catch (err: unknown) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        status: 'error',
        duration: 4000
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outline" h="100%">
      <CardBody>
        <HStack mb={3} spacing={2} justify="space-between">
          <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em">Static Ad Pipeline</Heading>
          <Badge colorScheme={selected === 'direct_image' ? 'green' : 'gray'}>{selected === 'direct_image' ? 'Default' : 'Fallback'}</Badge>
        </HStack>
        <VStack align="stretch" spacing={3}>
          <Select size="sm" value={selected} onChange={e => setSelected(e.target.value as Pipeline)}>
            {(Object.keys(LABELS) as Pipeline[]).map(value => <option key={value} value={value}>{LABELS[value]}</option>)}
          </Select>
          <Text fontSize="xs" color="brand.muted">
            Direct image sends the approved concept and the product photo to the image model,
            which composes and typesets the finished ad in one pass. The brand logo is added
            afterwards and is never sent to the model. Takes effect on the next new static ad.
          </Text>
          <Button size="sm" variant="outline" onClick={save} isLoading={busy} isDisabled={selected === persisted}>
            Save
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
}
